import axiosClient from "../../../shared/lib/http/axiosClient";
import { Item, ItemAdd, ItemEdit, ItemSearch, ItemsPaginatedSchema, ItemsResponse } from "@models/item";
import { buildSpatieSort } from "../utils/buildSpatieSort";
import { normalizeCustomDataFilter } from "../utils/normalizeCustomDataFilter";


type ItemInclude = "customData" | "entries";

export type ItemSortField =
  | "id"
  | "sku"
  | "name"
  | "group"
  | "manufacturer"
  | "model"
  | "price"
  | "cost"
  | "created_at"
  | "updated_at";

export type ItemSort = ItemSortField | `-${ItemSortField}`;

const ITEM_SORT_FIELDS: ItemSortField[] = [
  "id",
  "sku",
  "name",
  "group",
  "manufacturer",
  "model",
  "price",
  "cost",
  "created_at",
  "updated_at",
];

const ITEM_BROWSE_INCLUDES: ItemInclude[] = ["customData", "entries"];
const ITEM_BROWSE_PER_PAGE = 10;

export interface ItemSearchParams {
  filter?: Partial<ItemSearch>;
  include?: ItemInclude[];
  sort?: ItemSort[];
  per_page?: number;
  cursor?: string;
}

function normalizeItemSearchFilter(filter: ItemSearch): ItemSearch {
  const custom_data = normalizeCustomDataFilter(filter.custom_data);
  const { custom_data: _drop, ...rest } = filter;
  return {
    ...rest,
    ...(custom_data !== undefined && { custom_data }),
  };
}

function getSearchBody(params: ItemSearchParams) {
  const { filter: rawFilter, include, sort, per_page = ITEM_BROWSE_PER_PAGE, cursor } = params;
  const filter = rawFilter ? normalizeItemSearchFilter(rawFilter) : undefined;

  return {
    ...(filter && Object.keys(filter).length > 0 && { filter }),
    ...(include?.length && { include: include.join(",") }),
    ...(sort?.length && { sort: sort.join(",") }),
    ...(per_page && { per_page }),
    ...(cursor && { cursor }),
  };
}

export const buildItemSort = (sortKey: string, order: "ASC" | "DESC"): ItemSort =>
  buildSpatieSort(sortKey, order, ITEM_SORT_FIELDS, "id") as ItemSort;

export const searchItems = async (
  params: ItemSearchParams = {},
  signal?: AbortSignal,
): Promise<ItemsResponse> => {
  const response = await axiosClient.post(
    "/api/items/search",
    getSearchBody({
      include: ITEM_BROWSE_INCLUDES,
      per_page: ITEM_BROWSE_PER_PAGE,
      ...params,
    }),
    { signal },
  );

  return ItemsPaginatedSchema.parse(response.data);
};

export const getItemById = async (id: string, signal?: AbortSignal): Promise<Item> => {

  const response = await axiosClient.get(
    `/api/items/${id}`,
    { signal },
  );

  return response.data;
};


export const saveItem = async (itemId: number, itemToSave: ItemEdit): Promise<Item> => {

  const response = await axiosClient.patch(
    `/api/items/${itemId}`,
    itemToSave,
  );

  return response.data;
};


export const getNextItemNumber = async (): Promise<number> => {

  const response = await axiosClient.get(
    "/api/items/next-number",
  );

  return response.data.next_item_index;
};


export const addItem = async (itemToAdd: ItemAdd): Promise<Item> => {

  const response = await axiosClient.post(
    "/api/items",
    itemToAdd,
  );

  return response.data;
};


export const deleteItem = async (itemId: number): Promise<void> => {

  await axiosClient.delete(
    `/api/items/${itemId}`,
  );
};
